<template>
  <base-slide-over ref="slideover" :title="title" size="5xl">
    <div class="tw-px-4 sm:tw-px-6 tw-mt-4" v-if="isLoading">
      <div class="tw-flex tw-my-8 tw-items-center" v-for="i in 3" :key="i">
        <div class="tw-w-1/2 tw-flex tw-items-center tw-justify-between">
          <div class="tw-flex tw-items-center">
            <base-skeleton-loader class="tw-w-3 tw-h-3" />
            <base-skeleton-loader class="tw-ml-3 tw-h-3 tw-w-56" />
          </div>
          <arrow-right-icon class="tw-w-5 tw-h-5 tw-text-gray-500 tw-ml-2 tw-mr-4" />
        </div>
        <base-skeleton-loader class="tw-flex-grow tw-h-3" />
      </div>
    </div>

    <div class="tw-px-4 sm:tw-px-6 tw-mt-4" v-else>
      <div class="tw-mb-4">
        <base-text-field v-model="searchQuery" placeholder="Search locations..." size="sm" @input="onSearchInput">
          <template #prefix>
            <search-icon class="tw-h-5 tw-w-5" />
          </template>
        </base-text-field>
      </div>

      <div class="tw-mt-4" v-if="isPaging">
        <div class="tw-flex tw-my-8 tw-items-center" v-for="i in skeletonCount" :key="i">
          <div class="tw-w-1/2 tw-flex tw-items-center tw-justify-between">
            <div class="tw-flex tw-items-center">
              <base-skeleton-loader class="tw-w-3 tw-h-3" />
              <base-skeleton-loader class="tw-ml-3 tw-h-3 tw-w-56" />
            </div>
            <arrow-right-icon class="tw-w-5 tw-h-5 tw-text-gray-500 tw-ml-2 tw-mr-4" />
          </div>
          <base-skeleton-loader class="tw-flex-grow tw-h-3" />
        </div>
      </div>

      <div v-else>
        <div v-for="location in pagedLocations" :key="location.businessLocationId" class="tw-flex tw-items-center tw-mb-4">
          <div class="tw-w-1/2 tw-flex tw-items-center tw-justify-between">
            <base-checkbox :value="location.businessLocationId" v-model="selectedLocations" :label="location.name" />
            <arrow-right-icon class="tw-w-5 tw-h-5 tw-text-gray-500 tw-ml-2 tw-mr-4" />
          </div>
          <base-select
            class="tw-flex-grow"
            :options="cachedOrderedPlans"
            label-key="name"
            value-key="businessPlanId"
            placeholder="Select a plan"
            :value="mappings[location.businessLocationId]"
            @input="handleInput(location.businessLocationId, $event)"
          />
        </div>
        <div v-if="pagedLocations.length === 0" class="tw-text-gray-500 tw-text-center tw-py-4">
          No locations found.
        </div>
      </div>
    </div>

    <template #fixed-bottom>
      <div class="tw-px-3 tw-flex tw-justify-between">
        <div class="tw-flex tw-items-center">
          <base-button class="tw-py-2 tw-mr-2" type="flat" @click="selectAllVisible">
            {{ areAllVisibleSelected ? "Deselect All" : "Select All" }}
          </base-button>
          <base-button class="tw-py-2" type="flat" @click="selectAllEntries">
            {{ areAllEntriesSelected ? `Deselect all entries (${selectedLocations.length})` : "Select all entries" }}
          </base-button>
        </div>
        <!-- Pagination controls -->
        <div class="tw-flex-1 tw-px-4">
          <base-pagination v-model="page" v-if="page.total > page.take" />
        </div>
        <div class="tw-py-2">
          <base-button type="flat" class="tw-w-auto tw-mr-3" @click="hide()">Cancel</base-button>
          <base-button :loading="isSaving" class="tw-w-auto" @click="save">Save</base-button>
        </div>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import { ArrowRightIcon, SearchIcon } from "@/assets/svgs";
import { orderBy } from "lodash";

export default {
  components: { ArrowRightIcon, SearchIcon },

  props: {
    businessId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      isLoadingMappings: false,
      isSaving: false,
      businessProgram: {},
      arePlansLoading: false,
      locations: [],
      plans: [],
      selectedLocations: [],
      mappings: {},
      cachedOrderedLocations: [],
      cachedOrderedPlans: [],
      lastLoadedProgramId: null,
      searchQuery: "",
      page: {
        take: 50,
        skip: 0,
        total: 0,
      },
      isPaging: false,
    };
  },

  computed: {
    skeletonCount() {
      if (this.page.total === 0) return this.page.take;
      return Math.min(this.page.take, this.page.total - this.page.skip);
    },
    isLoading() {
      return this.arePlansLoading || this.isLoadingMappings;
    },
    title() {
      return `${this.businessProgram.name} Mappings`;
    },
    areAllEntriesSelected() {
      return this.locations.length > 0 && this.selectedLocations.length === this.locations.length;
    },
    areAllVisibleSelected() {
      if (this.pagedLocations.length === 0) return false;
      return this.pagedLocations.every((l) => this.selectedLocations.includes(l.businessLocationId));
    },
    selectedSet() {
      return new Set(this.selectedLocations);
    },
    filteredLocations() {
      if (!this.searchQuery || !this.searchQuery.trim()) return this.cachedOrderedLocations;
      const q = this.searchQuery.toLowerCase();
      return this.cachedOrderedLocations.filter((l) => l.name.toLowerCase().includes(q));
    },
    pagedLocations() {
      const start = this.page.skip;
      return this.filteredLocations.slice(start, start + this.page.take);
    },
  },

  watch: {
    locations(val) {
      this.cachedOrderedLocations = orderBy(val, ["name"], ["asc"]);
      this.page.total = this.filteredLocations.length;
    },
    plans(val) {
      this.cachedOrderedPlans = orderBy(val, ["name"], ["asc"]);
    },
    page: {
      deep: true,
      handler(newVal, oldVal) {
        if (oldVal && (newVal.skip !== oldVal.skip || newVal.take !== oldVal.take)) {
          this.isPaging = true;
          setTimeout(() => {
            this.isPaging = false;
          }, 300);
        }
      },
    },
  },

  methods: {
    onSearchInput() {
      this.page.skip = 0;
      this.page.total = this.filteredLocations.length;
    },
    async show(businessProgram) {
      this.businessProgram = businessProgram;
      this.$refs.slideover.show();
      this.selectedLocations = [];
      this.searchQuery = "";
      this.page.skip = 0;

      const promises = [];
      if (this.lastLoadedProgramId !== businessProgram.businessProgramId) {
        promises.push(this.getMappings());
      }
      if (this.plans.length === 0) promises.push(this.getPlans());

      await Promise.all(promises);
    },
    hide() {
      this.$refs.slideover.hide();
    },
    async getMappings() {
      try {
        this.isLoadingMappings = true;
        const { data } = await this.$axios.get(`/businesses/${this.businessId}/programs/${this.businessProgram.businessProgramId}/mappings`);
        this.locations = data.map((m) => ({
          businessLocationId: m.businessLocationId,
          name: m.locationName,
        }));
        this.mappings = Object.fromEntries(data.filter((m) => m.businessPlanId).map((m) => [m.businessLocationId, m.businessPlanId]));
        this.lastLoadedProgramId = this.businessProgram.businessProgramId;
      } finally {
        this.isLoadingMappings = false;
      }
    },
    async getPlans() {
      try {
        this.arePlansLoading = true;
        const { data } = await this.$axios.get(`/businesses/${this.businessId}/plans/names`);
        this.plans = data;
      } finally {
        this.arePlansLoading = false;
      }
    },
    async save() {
      try {
        this.isSaving = true;
        await this.$axios.post(`/businesses/${this.businessId}/programs/${this.businessProgram.businessProgramId}/mappings`, { mappings: this.mappings });
        this.hide();
        this.selectedLocations = [];
        this.mappings = {};
        this.lastLoadedProgramId = null;
      } catch (error) {
        this.EventBus.$emit("GLOBAL_ERROR", {
          title: "Unable to save mappings",
          message: `We were not able to save the mappings for this prescription program. There may have been a connection error. Please reload the page to try again.`,
          error,
        });
        throw error;
      } finally {
        this.isSaving = false;
      }
    },
    handleInput(businessLocationId, businessPlanId) {
      if (this.selectedSet.has(businessLocationId)) {
        const patch = Object.fromEntries(this.selectedLocations.map((id) => [id, businessPlanId]));
        this.mappings = { ...this.mappings, ...patch };
      } else {
        this.mappings = { ...this.mappings, [businessLocationId]: businessPlanId };
      }
    },
    selectAllEntries() {
      this.selectedLocations = this.areAllEntriesSelected ? [] : this.locations.map((l) => l.businessLocationId);
    },
    selectAllVisible() {
      const visibleIds = this.pagedLocations.map((l) => l.businessLocationId);
      if (this.areAllVisibleSelected) {
        this.selectedLocations = this.selectedLocations.filter((id) => !visibleIds.includes(id));
      } else {
        const newSelected = new Set([...this.selectedLocations, ...visibleIds]);
        this.selectedLocations = Array.from(newSelected);
      }
    },
  },
};
</script>
